// Composables
import { createRouter, createWebHistory } from 'vue-router';
import nhost from '@/plugins/nhost';
import SignIn from '@/views/auth/SignIn.vue';
import ForgotPassword from '@/views/auth/ForgotPassword.vue';
import SignUp from '@/views/auth/SignUp.vue';
import Logout from '@/views/auth/Logout.vue';
import ChangePassword from '@/views/auth/ChangePassword.vue';
import FirstAccess from '@/views/auth/FirstAccess.vue';
import AcceptInvestorInvite from '@/views/auth/AcceptInvestorInvite.vue';
import AccessPending from '@/views/auth/AccessPending.vue';
import { gql } from '@apollo/client/core';
import { apolloClient } from '@/plugins/nhost';
import { useClientStore } from '@/store/client';
import { useDataroomModuleConfigStore } from '@/store/dataroomModuleConfig';

const routes = [
  {
    path: '/',
    name: 'root',
    component: () => import(/* webpackChunkName: "public" */ '@/layouts/public/Default.vue'),
    children: [
      {
        path: '',
        name: 'signin-redirect',
        redirect: {
          name: 'signin',
        },
      },
      {
        path: 'sign-in',
        name: 'signin',
        component: SignIn,
      },
      {
        path: 'sign-up',
        name: 'signup',
        component: SignUp,
      },
      {
        path: 'forgot-password',
        name: 'forgotpassword',
        component: ForgotPassword,
      },
      {
        path: 'change-password',
        name: 'changepassword',
        component: ChangePassword,
      },
      {
        path: 'first-access',
        name: 'firstaccess',
        component: FirstAccess,
      },
      // TECH-2672 — a personal investor invitation accepted on this company's own domain. Public:
      // the page has to work before the invitee has any account at all.
      {
        path: 'investor-invite/:token',
        name: 'investorInviteAccept',
        component: AcceptInvestorInvite,
      },
      // Reached by the guard below when the share exists but no module is released yet.
      {
        path: 'access-pending',
        name: 'accessPending',
        component: AccessPending,
      },
    ],
  },
  {
    path: '/app',
    name: 'app',
    component: () => import(/* webpackChunkName: "private" */ '@/layouts/private/Default.vue'),
    meta: {
      auth: true,
    },
    children: [
      {
        path: '',
        name: 'appHome',
        redirect: {
          name: 'dashboard',
        },
      },
      {
        path: 'cockpit',
        name: 'cockpitMenu',
        children: [
          {
            path: '',
            name: 'cockpitHome',
            component: () => import(/* webpackChunkName: "cockpit" */ '@/views/cockpit/Home.vue'),
          },
          {
            path: 'financial',
            name: 'cockpitMenuFinancial',
            component: () => import(/* webpackChunkName: "cockpit-financial" */ '@/views/cockpit/Financial/Home.vue'),
            meta: { requiresModule: 'enableFinancialModule' },
          },
          {
            path: 'accounting',
            name: 'cockpitMenuAccounting',
            component: () => import(/* webpackChunkName: "cockpit-accounting" */ '@/views/cockpit/Accounting/Home.vue'),
            meta: { requiresModule: 'enableAccountingModule' },
          },
          {
            path: 'indicators',
            name: 'cockpitMenuIndicators',
            component: () => import(/* webpackChunkName: "cockpit-indicators" */ '@/views/cockpit/Indicators/Home.vue'),
            meta: { requiresModule: 'enableIndicatorsModule' },
          },
        ],
      },
      {
        path: 'company-area',
        name: 'companyArea',
        component: () => import(/* webpackChunkName: "company-area" */ '@/views/companyArea/Home.vue'),
        meta: { requiresModule: 'enableCompanyAreaModule' },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import(/* webpackChunkName: "dashboard" */ '@/views/analytics/Dashboard.vue'),
      },
      {
        path: 'logout',
        name: 'logout',
        component: Logout,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const GET_USER_ACCESS_TO_CLIENT = gql`
  query GetUserAccessToClient($domain: String!) {
    general_module_configuration_dataroom(where: { custom_domain: { _eq: $domain } }) {
      custom_domain
      client_id
    }
  }
`;

// TECH-2672 — what grants access to this domain's dataroom is `general.view_user_client_dataroom`,
// the same view every `user-dataroom` permission filters by, NOT membership in the company.
// Membership is only one of its branches; a personal account reaches the dataroom through the
// portfolio share instead (branch C of the view), and the old `general_user_client` check locked
// exactly those people out. Queried as `user-dataroom` (this app's role), so the row coming back is
// already filtered to the caller — a result means "this dataroom is shared with me".
const CHECK_DATAROOM_ACCESS = gql`
  query CheckDataroomAccess($clientId: uuid!) {
    general_client(where: { id: { _eq: $clientId } }) {
      id
    }
  }
`;

// Distinguishes "no relationship at all" from "share exists, modules not released yet": the second
// gets the access-pending page instead of being signed out.
const CHECK_PENDING_SHARE = gql`
  query CheckPendingShare($clientId: uuid!) {
    portfolio_organization_client_module_authorization(where: { client_id: { _eq: $clientId } }) {
      id
      dataroom_enabled
    }
  }
`;

const INSERT_USER_EVENT = gql`
  mutation create($event_name: String, $extra_fields: jsonb) {
    insert_user_analysis_user_events_one(object: { event_name: $event_name, extra_fields: $extra_fields }) {
      event_name
    }
  }
`;

router.beforeEach(async to => {
  const guardStart = performance.now();
  const lap = (label: string, from: number) => {
    const now = performance.now();
    console.log(`[LogoPerf] router guard :: ${label} took ${(now - from).toFixed(1)}ms (total ${(now - guardStart).toFixed(1)}ms)`);
    return now;
  };

  let t = performance.now();
  const authenticated = await nhost.auth.isAuthenticatedAsync();
  t = lap('isAuthenticatedAsync', t);

  if (!authenticated && to.meta.auth) {
    return '/sign-in';
  }

  // If user is authenticated and accessing /app routes, ensure client matches domain
  if (authenticated && to.meta.auth) {
    const clientStore = useClientStore();

    try {
      // Always check if domain has a corresponding client (whiteLabelLogin scenario)
      t = performance.now();
      const domainResult = await apolloClient.query({
        query: GET_USER_ACCESS_TO_CLIENT,
        variables: {
          domain: window.location.hostname,
        },
        fetchPolicy: 'network-only', // Always fetch fresh data
      });
      t = lap('GET_USER_ACCESS_TO_CLIENT query', t);

      if (domainResult.data.general_module_configuration_dataroom.length > 0) {
        const domainClientId = domainResult.data.general_module_configuration_dataroom[0].client_id;
        t = performance.now();
        const currentClient = await clientStore.getSelectedClient();
        t = lap('getSelectedClient (localStorage)', t);

        // If selected client doesn't match domain client, validate access and update
        if (!currentClient || currentClient.id !== domainClientId) {
          t = performance.now();
          const user = await nhost.auth.getUser();
          t = lap('nhost.auth.getUser', t);

          if (user?.id) {
            // Check if the dataroom of this domain is shared with the caller
            t = performance.now();
            const accessResult = await apolloClient.query({
              query: CHECK_DATAROOM_ACCESS,
              variables: {
                clientId: domainClientId,
              },
              fetchPolicy: 'network-only',
            });
            t = lap('CHECK_DATAROOM_ACCESS query', t);

            if (accessResult.data.general_client.length > 0) {
              // User has access, update the selected client to match domain
              console.log(`[Router Guard] Updating client to match domain: ${domainClientId}`);
              t = performance.now();
              await clientStore.setSelectedClientId(domainClientId);
              lap('setSelectedClientId (GET_COMPANY_QUERY)', t);
            } else {
              // No dataroom access. Before signing anyone out, tell the two cases apart: an invitee
              // whose company has not released the module yet is in the *expected* first state
              // (accepting grants no module), and signing them out looks like a broken login.
              const pendingShare = await apolloClient.query({
                query: CHECK_PENDING_SHARE,
                // Only `client_id` is filtered here: the `user-dataroom` select permission on this
                // table already restricts rows to ACTIVE shares granted to organizations of clients
                // the caller belongs to. Repeating either condition in the query would mean exposing
                // `organization`/`active` to this role for no gain.
                variables: { clientId: domainClientId },
                fetchPolicy: 'network-only',
              }).catch(() => null);

              const hasPendingShare = (pendingShare?.data?.portfolio_organization_client_module_authorization?.length ?? 0) > 0;
              if (hasPendingShare) {
                console.warn(`[Router Guard] Share exists but no module released for domain: ${window.location.hostname}`);
                return { name: 'accessPending' };
              }

              console.error(`[Router Guard] User does not have access to client for domain: ${window.location.hostname}`);
              await nhost.auth.signOut();
              return '/sign-in';
            }
          }
        }
      } else {
        // No client configured for this domain, check if client is set
        const currentClient = await clientStore.getSelectedClient();
        if (!currentClient) {
          console.warn('[Router Guard] No client configured for this domain and no client selected');
        }
      }
    } catch (e) {
      console.error('[Router Guard] Error validating/updating client:', e);
    }

    if (to.meta.requiresModule) {
      try {
        const currentClient = await clientStore.getSelectedClient();
        if (currentClient?.id) {
          const dataroomModuleConfigStore = useDataroomModuleConfigStore();
          await dataroomModuleConfigStore.fetchConfig(currentClient.id);
          if (!dataroomModuleConfigStore.isModuleEnabled(to.meta.requiresModule as string)) {
            return { name: 'cockpitHome' };
          }
        }
      } catch (e) {
        console.error('[Router Guard] Error checking dataroom module visibility:', e);
      }
    }
  }

  try {
    nhost.graphql.request(INSERT_USER_EVENT, {
      event_name: to.fullPath,
      extra_fields: {
        host: 'plataforma-dataroom-web',
      },
    });
  } catch {
    /* empty */
  }

  console.log(`[LogoPerf] router guard :: TOTAL ${(performance.now() - guardStart).toFixed(1)}ms for ${to.fullPath}`);
  return true;
});

export const menuItems = [
  {
    name: 'dashboard',
    route: '/app/dashboard',
    icon: 'FundOutlined',
    iconActive: 'FundFilled',
    disabled: false,
  },
  {
    name: 'cockpit',
    route: '/app/cockpit',
    icon: 'DashboardOutlined',
    iconActive: 'DashboardFilled',
    disabled: false,
  },
  {
    name: 'company-area',
    route: '/app/company-area',
    icon: 'ExportOutlined',
    iconActive: 'ExportFilled',
    disabled: false,
  },
  {
    name: 'logout',
    route: '/app/logout',
    icon: 'LogoutOutlined',
    iconActive: 'LogoutFilled',
    disabled: false,
  },
];

export default router;
